import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6 py-12">
        <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-display font-bold text-slate-900">Invalid link</h1>
          <p className="text-slate-600 mt-2">This reset link is missing a token.</p>
          <Link to="/forgot-password"><Button variant="outline" className="mt-6">Request a new link</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8">
        {done ? (
          <div className="text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h1 className="text-2xl font-display font-bold text-slate-900">Password reset</h1>
            <p className="text-sm text-slate-600 mt-2" data-testid="reset-password-success">Redirecting to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-display font-bold tracking-tight text-slate-900">Choose a new password</h1>
            {err && <Alert variant="destructive" className="mt-4" data-testid="reset-password-error"><AlertDescription>{err}</AlertDescription></Alert>}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" required minLength={8} value={password}
                  onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="pw2">Confirm new password</Label>
                <Input id="pw2" type="password" required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} data-testid="reset-password-confirm-input" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-submit-button">
                {loading ? "Resetting…" : "Reset password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
